import { useLanguage, useSEO } from "../context/LanguageContext";
import { PageHero, Reveal, Overline } from "../components/Kinetic";
import { SubmissionForm } from "../components/SubmissionForm";

const ProgrammePage = () => {
  const { t } = useLanguage();
  useSEO(t("programme.metaTitle"), t("programme.metaDesc"));
  const domains = t("programme.domains");
  const labels = t("programme.labels");

  const fields = [
    { name: "name", label: t("forms.name"), type: "text" },
    { name: "email", label: t("forms.email"), type: "email" },
    { name: "organization", label: t("forms.organization"), type: "text", required: false },
    { name: "proposalTitle", label: t("forms.proposalTitle"), type: "text" },
    { name: "message", label: t("forms.message"), type: "textarea" },
  ];

  const row = (label, value, testid) => (
    <div className="grid gap-2 border-b border-graphite/10 py-5 md:grid-cols-4" data-testid={testid}>
      <p className="overline-mono text-forest">{label}</p>
      <div className="md:col-span-3">
        {Array.isArray(value) ? (
          <ul className="space-y-1.5">
            {value.map((v) => (
              <li key={v} className="text-sm md:text-base leading-relaxed text-graphite-soft">
                — {v}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm md:text-base leading-relaxed text-graphite-soft">{value}</p>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="programme-page">
      <PageHero
        overline={t("programme.overline")}
        titleLines={t("programme.titleLines")}
        intro={t("programme.intro")}
        testid="programme"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-5xl space-y-20 px-6">
          {domains.map((d, i) => (
            <Reveal key={d.title} data-testid={`programme-domain-${i}`}>
              <article data-testid={`programme-domain-${i}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-sm text-forest">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-3xl md:text-4xl font-light tracking-tight text-graphite">{d.title}</h2>
                </div>
                <div className="mt-8 border-t border-graphite/15">
                  {row(labels.problematique, d.problematique, `domain-${i}-problematique`)}
                  {row(labels.questions, d.questions, `domain-${i}-questions`)}
                  {row(labels.methodologie, d.methodologie, `domain-${i}-methodologie`)}
                  {row(labels.donnees, d.donnees, `domain-${i}-donnees`)}
                  {row(labels.collaborations, d.collaborations, `domain-${i}-collaborations`)}
                  {row(labels.resultats, d.resultats, `domain-${i}-resultats`)}
                </div>
              </article>
            </Reveal>
          ))}
          <Reveal>
            <p className="border-l-2 border-forest pl-6 text-sm leading-relaxed text-graphite-soft">
              {t("programme.honestyNote")}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-graphite/10 bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Reveal y={16}>
              <Overline>{t("programme.formOverline")}</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-3xl md:text-5xl font-light tracking-tight text-graphite">
                {t("programme.formTitle")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-graphite-soft">{t("programme.formText")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-3">
            <SubmissionForm formType="project" fields={fields} submitLabel={t("programme.formSubmit")} testid="project" />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ProgrammePage;
